import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { TypeMenu } from "@/Models/Menu";
import { Axios } from "@/Services/Axios";
import { useQuery } from "@tanstack/react-query";
import { TypePerfil } from "@/Models/Pessoa/Perfil";
import { Funcionalidade } from "@/Models/Sistema/Funcionalidade";
import { InferQueryTypeFromAxiosFn } from "@/Utils/RequisicaoUtilitario";

export class PerfilService {
    public static listar(perfil: TypePerfil): Promise<AxiosResponse<TypePerfil[]>> {
        return Axios.get<TypePerfil[]>(`perfil`, { params: perfil });
    }

    public static listarPermissoes(codigoDoPerfil: number){
        return Axios.get<Funcionalidade[]>(`perfil/permissoes/${codigoDoPerfil}`);
    }

    public static listarMenus(codigoDoPerfil: number){
        return Axios.get<TypeMenu[]>(`perfil/menus/${codigoDoPerfil}`);
    }

    public static listarUsuarios({ codPerfil }: { codPerfil: number }){
        return Axios.get<number[]>(`perfil/usuarios/${codPerfil}`);
    }

    public static inserir(perfil: TypePerfil){
        return Axios.post<boolean>(`perfil`, perfil);
    }

    public static atualizar(perfil: TypePerfil){
        return Axios.put<boolean>(`perfil`, perfil);
    }

    public static ativar(codigo: number){
        return Axios.patch<boolean>(`perfil/ativar/${codigo}`);
    }

    public static desativar(codigo: number){
        return Axios.patch<boolean>(`perfil/desativar/${codigo}`);
    }

    public static atualizarPermissoes(codigoDoPerfil: number, novasPermissoes: string[] ){
        return Axios.put<void>(`perfil/permissoes/${codigoDoPerfil}`, novasPermissoes);
    }

    public static atualizarMenus(codigoDoPerfil: number, novosMenus: string[] ){
        return Axios.put<void>(`perfil/menus/${codigoDoPerfil}`, novosMenus);
    }

    public static adicionarUsuarios(codPerfil: number, usuarios: number[]){
        return Axios.post<number[]>(`perfil/usuarios/${codPerfil}`, usuarios);
    }

    public static removerUsuarios(codPerfil: number, usuarios: number[]){
        return Axios.delete<number[]>(`perfil/usuarios/${codPerfil}`, { data: usuarios });
    }

}

export const useListarPerfis = ({config, params}: InferQueryTypeFromAxiosFn<typeof PerfilService.listar>) => {
    const query = useQuery({
        queryKey: ["Perfil", "Perfil.Listar", params],
        queryFn: async () => {
            return PerfilService.listar(...params);
        },
        ...config,
    });

    return query;
}

export const useListarPermissoes = ({config, params}: InferQueryTypeFromAxiosFn<typeof PerfilService.listarPermissoes>) => {
    const query = useQuery({
        queryKey: ["Perfil", "Perfil.Permissoes", params],
        queryFn: async () => {
            return PerfilService.listarPermissoes(...params);
        },
        ...config,
    });

    return query;
}

export const useListarMenus = ({config, params}: InferQueryTypeFromAxiosFn<typeof PerfilService.listarMenus>) => {
    const query = useQuery({
        queryKey: ["Perfil", "Perfil.Menus", params],
        queryFn: async () => {
            return PerfilService.listarMenus(...params);
        },
        ...config,
    });

    return query;
}

export const useBuscarUsuariosDoPerfil = ({config, params}: InferQueryTypeFromAxiosFn<typeof PerfilService.listarUsuarios>) => {
    const query = useQuery({
        queryKey: ["Perfil", "Perfil.Usuarios", params],
        queryFn: async () => {
            return PerfilService.listarUsuarios(...params);
        },
        ...config,
    });
    return query;
}

export const useBuscarPerfil = (codPerfil: number, enabled: boolean = true) => {
    const [perfilEncontrado, setPerfilEncontrado] = useState<TypePerfil | null>(null);
    const { data: perfisData, ...perfisQuery } = useListarPerfis({ config: { enabled: enabled, gcTime: 0 }, params: [{ codigo: codPerfil }] });

    useEffect(() => {
        const perfil = perfisData?.data.at(0);

        if (codPerfil && perfil) {
            setPerfilEncontrado(perfil);
        }
    }, [perfisData]);

    return {
        perfil: perfilEncontrado, 
        query: perfisQuery
    }
}